import { useCallback, useRef } from "react";
import { toast } from "react-toastify";

import { Item } from "@glideapps/glide-data-grid";
import { useAtomValue, useSetAtom } from "jotai";

import {
  changedCellsAtom,
  columnsAtom,
  filteredUwisAtom
} from "../atoms/fieldGroupAtoms";
import { addCellsToChangeMap } from "../atoms/utils/cellTransformers";
import { MAX_PAYLOAD_SIZE } from "./useFieldGroupSave";

/**
 * Estimates the JSON payload size of the changes
 */
export function estimatePayloadSize(
  changes: Array<{ propertyName: string; uwi: string; value: string }>
): number {
  // Rough estimate: each change has propertyName + uwi + value + JSON overhead (~50 bytes)
  return changes.reduce((total, change) => {
    const size =
      change.propertyName.length + change.uwi.length + change.value.length + 50;
    return total + size;
  }, 0);
}

/**
 * Hook for handling grid paste operations
 */
export function useGridPaste() {
  const columns = useAtomValue(columnsAtom);
  const uwis = useAtomValue(filteredUwisAtom);
  const setColumns = useSetAtom(columnsAtom);
  const setChangedCells = useSetAtom(changedCellsAtom);
  const headerClickedRef = useRef(false);

  const onHeaderClicked = useCallback(() => {
    headerClickedRef.current = true;
  }, []);

  const onPaste = useCallback(
    (target: Item, values: readonly (readonly string[])[]): boolean => {
      const [targetCol, targetRow] = target;

      const startRowOffset = headerClickedRef.current ? 1 : 0;
      headerClickedRef.current = false;

      const columnsToUpdate = new Map<number, Map<string, string>>();
      const changes: Array<{ propertyName: string; uwi: string; value: string }> = [];

      // Process all paste values in single pass
      for (let rowOffset = startRowOffset; rowOffset < values.length; rowOffset++) {
        const currentRow = targetRow + (rowOffset - startRowOffset);
        if (currentRow < 0 || currentRow >= uwis.length) continue;

        const uwi = uwis[currentRow];
        if (!uwi) continue;

        const rowValues = values[rowOffset];

        for (
          let pasteValueIndex = 0;
          pasteValueIndex < rowValues.length;
          pasteValueIndex++
        ) {
          // Calculate which column this value is intended for
          const intendedCol = targetCol + pasteValueIndex;

          // Skip if intended column is UWI (col 0) - it's readonly
          // This skips both the column AND the corresponding paste value
          if (intendedCol === 0) continue;

          const dataColIndex = intendedCol - 1;
          if (dataColIndex < 0 || dataColIndex >= columns.length) continue;

          const column = columns[dataColIndex];
          const newValue = rowValues[pasteValueIndex] ?? "";

          // Update columnsToUpdate map
          if (!columnsToUpdate.has(dataColIndex)) {
            columnsToUpdate.set(dataColIndex, new Map());
          }
          const columnMap = columnsToUpdate.get(dataColIndex);
          if (columnMap) {
            columnMap.set(uwi, newValue);
          }

          // Track changes
          changes.push({ propertyName: column.propertyName, uwi, value: newValue });
        }
      }

      if (changes.length === 0) return false;

      // Check if payload size exceeds limit
      const estimatedSize = estimatePayloadSize(changes);
      if (estimatedSize > MAX_PAYLOAD_SIZE) {
        toast.error(
          `Paste operation too large (${(estimatedSize / 1024 / 1024).toFixed(
            1
          )}MB). Maximum allowed is 50MB. Try pasting smaller sections.`,
          { autoClose: 8000 }
        );
        return false;
      }

      // Apply updates to columns
      const updatedColumns = columns.map((col, index) => {
        const columnUpdates = columnsToUpdate.get(index);
        if (!columnUpdates) return col;

        const newData = { ...col.data };
        columnUpdates.forEach((value, uwi) => {
          newData[uwi] = value;
        });

        return { ...col, data: newData };
      });

      setColumns(updatedColumns);
      setChangedCells((prev) => addCellsToChangeMap(prev, changes));

      return false;
    },
    [columns, uwis, setColumns, setChangedCells]
  );

  return { onPaste, onHeaderClicked };
}
